import React, { useState, useEffect, useCallback, useRef } from "react";
import { Location } from "@/types";
import { useSelectedDayItems } from "@/hooks/useSelectedDayItems";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useExperiencesStore from "@/stores/tripBuilder/useExperiences";
import ActivityContainer from "@tripbuilder/cat-schedule/ActivityContainer";
import DayNavigation from "@tripbuilder/cat-schedule/DayNavigation";
import { isSameDay } from "date-fns";
import ActivityPlaceholder from "./AddingPlaceholder";
import logger from "@/utils/logger";

const McSchedule: React.FC = () => {
  const {
    selectedLocation,
    selectedDay,
    setSelectedDay,
    tripData,
    updateTripData,
  } = useTripStore();

  const selectedDayItems = useSelectedDayItems(
    selectedDay || "",
    selectedLocation?.id
  );

  // experiences store
  const addingExperience = useExperiencesStore(
    (state) => state.addingExperience
  );

  const handleSelect = useCallback(
    (date: string) => {
      setSelectedDay(date);
    },
    [setSelectedDay]
  );

  const handleDeleteActivity = useCallback(
    async (id: string, name: string) => {
      if (!tripData || !selectedLocation || !selectedDay) return;

      const updatedLocations = tripData.locations.map((loc) => {
        if (loc.id === selectedLocation.id) {
          const updatedItineraryItems = loc.itinerary_items.map((dayItem) => {
            if (isSameDay(dayItem.date, selectedDay)) {
              return {
                ...dayItem,
                items: dayItem.items.filter(
                  (item) => !(item.id === id && item.name === name)
                ),
              };
            }
            return dayItem;
          });

          // Create location update for activity removal
          useTripStore
            .getState()
            .createLocationUpdate(
              selectedLocation.waytora_id,
              selectedLocation.id,
              "experience_removed",
              { itemName: name, itemType: "experience" }
            );

          return { ...loc, itinerary_items: updatedItineraryItems };
        }
        return loc;
      });

      updateTripData({ ...tripData, locations: updatedLocations });
    },
    [selectedDay, tripData, updateTripData, selectedLocation]
  );

  // Navigation state and handlers
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonsWrapperRef = useRef<HTMLDivElement>(null);
  const [offsetX, setOffsetX] = useState(0);
  const [maxOffsetX, setMaxOffsetX] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Memoize navigation handlers
  const handleNext = useCallback(() => {
    setOffsetX((prevOffset) => {
      const newOffset = prevOffset + containerWidth * 0.8;
      return newOffset > maxOffsetX ? maxOffsetX : newOffset;
    });
  }, [containerWidth, maxOffsetX]);

  const handlePrev = useCallback(() => {
    setOffsetX((prevOffset) => {
      const newOffset = prevOffset - containerWidth * 0.8;
      return newOffset < 0 ? 0 : newOffset;
    });
  }, [containerWidth]);

  // Update container dimensions
  useEffect(() => {
    const updateWidths = () => {
      if (containerRef.current && buttonsWrapperRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const buttonsWidth = buttonsWrapperRef.current.scrollWidth;
        const maxOffset = buttonsWidth - containerWidth;
        setMaxOffsetX(maxOffset > 0 ? maxOffset : 0);
        setContainerWidth(containerWidth);
        setIsOverflowing(buttonsWidth > containerWidth);
      }
    };

    const resizeObserver = new ResizeObserver(updateWidths);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [selectedLocation]);

  logger.log("Selected Day Items", selectedDayItems);

  if (!tripData || !selectedLocation || !selectedDay) return null;

  return (
    <div className="flex flex-col w-full relative">
      <DayNavigation
        selectedLocation={selectedLocation}
        selectedDay={selectedDay}
        handleSelect={handleSelect}
        containerRef={containerRef}
        buttonsWrapperRef={buttonsWrapperRef}
        offsetX={offsetX}
        maxOffsetX={maxOffsetX}
        containerWidth={containerWidth}
        isOverflowing={isOverflowing}
        onNext={handleNext}
        onPrev={handlePrev}
      />
      <div
        className="mt-2 2xl:mt-3 rounded-md relative w-full h-full flex flex-col overflow-y-auto no-scrollbar"
        style={{ height: "calc(100vh - 14rem)" }}
      >
        <ActivityContainer
          selectedLocation={selectedLocation as Location}
          selectedDay={selectedDay}
          activities={selectedDayItems}
          onDelete={handleDeleteActivity}
        />
      </div>
      {addingExperience && (
        <ActivityPlaceholder experience={addingExperience} />
      )}
    </div>
  );
};

export default React.memo(McSchedule);
